/* RatingsReviewsScreen.js — Full Customer Reviews & Reputation Management
   ────────────────────────────────────────────────────────────────────────
   FULL FEATURE SET:
     • 5 tabs: Overview · Reviews · Analytics · Insights · Settings
     • Auto-sentiment classification from rating (positive/neutral/negative)
     • Pulse Quality Score — composite metric from rating, response, verification
     • Real bar/sparkline charts (pure RN Views — no chart library needed)
     • "Needs Response" filter — surfaces reviews requiring action
     • Search bar with keyword highlight
     • 6 sort options (newest, oldest, highest, lowest, most-helpful, needs-reply)
     • Drill-down review detail modal with full history
     • Reply with 6 quick templates + AI-suggested response
     • Edit / delete reply after posting
     • Helpful counter actually increments
     • Admin verify / unverify toggle, mark published / pending
     • Bulk actions (select-multiple → bulk reply / verify / delete)
     • Review request workflow — email client asking for a review
     • CSV + plain-text export via native Share
     • Source tracking (internal/Google/Facebook/email)
     • Auto-extracted keyword cloud from review text
     • Funnel chart (received → verified → published → replied)
     • Category ratings with bars (not just numbers)
     • Time period selector for analytics (week/month/quarter/year)
     • FAB with quick actions (request, add, export, settings)
     • Real persistent settings (notifications, auto-response, moderation)
     • Auto-response template editor
     • Defensive `company_id || 0` (prevents cross-company bleed)
     • Multi-token auth (auth_token | userToken | token)
     • Dynamic company name from profile — no hardcoded JOBENG
*/

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, Dimensions, Alert, Image,
  Modal, TextInput, ActivityIndicator, RefreshControl, StatusBar, FlatList,
  Platform, Share, Animated, Easing, ScrollView, KeyboardAvoidingView, Linking,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const { width } = Dimensions.get('window');

// ── Brand palette ──────────────────────────────────────────────────────────
const PRIMARY    = '#0e4e96';
const PRIMARY_2  = '#1e7be2';
const SUCCESS    = '#10B981';
const WARNING    = '#F59E0B';
const DANGER     = '#EF4444';
const INFO       = '#0891B2';
const PURPLE     = '#8B5CF6';
const INDIGO     = '#6366F1';
const NEUTRAL    = '#64748B';
const BORDER     = '#E2E8F0';
const BG         = '#F8FAFC';
const SURFACE    = '#FFFFFF';

// ── API ────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY      = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getCompanyId = async () => {
  try {
    // Canonical key first (per CLAUDE.md)
    const raw = await AsyncStorage.getItem('company_id');
    if (raw) {
      const n = parseInt(raw);
      if (n > 0) return n;
    }
    // Fallback: derive from currentUser or companyProfile
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const u = JSON.parse(userData);
      const id = u.company_id || u.companyId;
      if (id) return parseInt(id);
    }
    const cp = await AsyncStorage.getItem('companyProfile');
    if (cp) {
      const c = JSON.parse(cp);
      const id = c.id || c.company_id;
      if (id) return parseInt(id);
    }
    return 0; // defensive default — prevents accidental cross-company queries
  } catch { return 0; }
};

const getAuthToken = async () => {
  try {
    return (await AsyncStorage.getItem('auth_token'))
      || (await AsyncStorage.getItem('userToken'))
      || (await AsyncStorage.getItem('token'))
      || null;
  } catch { return null; }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    const token     = await getAuthToken();
    const [path, queryString] = endpoint.split('?');
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId > 0) params.set('company_id', String(companyId));
    const sep = params.toString() ? '?' : '';
    const url = `${baseUrl}${sep}${params.toString()}`;

    const headers = {
      'Content-Type': 'application/json',
      'X-API-Key':    API_KEY,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const response = await fetch(url, {
      headers,
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });
    if (!response.ok) throw new Error(`API ${response.status}`);
    return await response.json();
  } catch (err) {
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log(`[Reviews] ${endpoint}:`, err?.message);
    }
    throw err;
  }
};

// ── Helpers ────────────────────────────────────────────────────────────────
const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';
const fmtDateShort = (d) => d
  ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  : '';
const daysSince = (d) => d
  ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000)
  : 0;
const relativeTime = (d) => {
  if (!d) return '';
  const diff = daysSince(d);
  if (diff === 0) return 'today';
  if (diff === 1) return 'yesterday';
  if (diff < 7)   return `${diff}d ago`;
  if (diff < 30)  return `${Math.floor(diff / 7)}w ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
};

const sentiment = (rating) => {
  const r = parseFloat(rating) || 0;
  if (r >= 4)   return { key: 'positive', label: 'Positive', color: SUCCESS, icon: 'emoticon-happy-outline' };
  if (r >= 3)   return { key: 'neutral',  label: 'Neutral',  color: WARNING, icon: 'emoticon-neutral-outline' };
  return        { key: 'negative', label: 'Negative', color: DANGER,  icon: 'emoticon-sad-outline' };
};

const STOPWORDS = new Set([
  'the','and','was','were','our','their','they','them','for','with','this','that','have',
  'has','had','will','would','been','being','from','about','into','your','you','just',
  'very','really','some','some','more','than','then','also','able','done','they','these',
  'are','was','what','when','who','where','why','how','but','not','can','any','all','out',
  'up','off','over','under','too','much','many','few','one','two','three','four','five',
  'team','project','work','time','service','company','great','good','bad','best','well',
]);

const extractKeywords = (reviews) => {
  const counts = {};
  reviews.forEach(rev => {
    const text = `${rev.review || ''} ${rev.title || ''}`.toLowerCase();
    const words = text.match(/[a-z]{4,}/g) || [];
    words.forEach(w => {
      if (STOPWORDS.has(w)) return;
      counts[w] = (counts[w] || 0) + 1;
    });
    (rev.tags || []).forEach(t => {
      const k = String(t).toLowerCase();
      counts[k] = (counts[k] || 0) + 2; // tags weighted higher
    });
  });
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 20)
    .map(([word, count]) => ({ word, count }));
};

const computeAvgResponseHours = (reviews) => {
  const withReply = reviews.filter(r => r.response?.date && r.date);
  if (withReply.length === 0) return null;
  const totalHours = withReply.reduce((s, r) => {
    const diff = (new Date(r.response.date).getTime() - new Date(r.date).getTime()) / 3600000;
    return s + Math.max(0, diff);
  }, 0);
  return totalHours / withReply.length;
};

const fmtHours = (h) => {
  if (h == null) return '—';
  if (h < 1)  return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${Math.round(h / 24)}d`;
};

// CSV helpers
const escapeCSV = (v) => {
  if (v == null) return '';
  const s = String(v);
  if (/[,"\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};

// ── DB ↔ UI transformers ───────────────────────────────────────────────────
// PostgreSQL uses snake_case columns; the UI uses camelCase.
// These translate between the two at the API boundary.
const dbToUi = (row) => row && ({
  id:             row.id,
  company_id:     row.company_id,
  client_id:      row.client_id,
  customerName:   row.customer_name,
  customerEmail:  row.customer_email,
  customerAvatar: row.customer_avatar,
  rating:         row.rating,
  title:          row.title,
  review:         row.review,
  date:           row.review_date,
  project:        row.project,
  project_id:     row.project_id,
  status:         row.status || 'pending',
  helpful:        row.helpful_count || 0,
  helpfulVoted:   false, // per-user state, not persisted on the row
  response:       row.response_text ? {
                    text:   row.response_text,
                    date:   row.response_date,
                    author: row.response_author,
                    edited: row.response_edited,
                  } : null,
  verified:       !!row.verified,
  source:         row.source || 'internal',
  tags:           Array.isArray(row.tags) ? row.tags : [],
});

const uiToDb = (item) => ({
  company_id:      item.company_id,
  client_id:       item.client_id || null,
  customer_name:   item.customerName || '',
  customer_email:  item.customerEmail || null,
  customer_avatar: item.customerAvatar || null,
  rating:          parseInt(item.rating) || 0,
  title:           item.title || null,
  review:          item.review || null,
  review_date:     item.date || new Date().toISOString().split('T')[0],
  project:         item.project || null,
  project_id:      item.project_id || null,
  status:          item.status || 'pending',
  helpful_count:   parseInt(item.helpful) || 0,
  response_text:   item.response?.text || null,
  response_date:   item.response?.date || null,
  response_author: item.response?.author || null,
  response_edited: item.response?.edited || null,
  verified:        !!item.verified,
  source:          item.source || 'internal',
  tags:            Array.isArray(item.tags) ? item.tags : [],
});

// ── Sub-components ─────────────────────────────────────────────────────────
const StarRating = ({ rating, size = 16, color = WARNING }) => (
  <View style={{ flexDirection: 'row', gap: 1 }}>
    {[1, 2, 3, 4, 5].map(i => (
      <Ionicons
        key={i}
        name={i <= rating ? 'star' : (i - 0.5 <= rating ? 'star-half' : 'star-outline')}
        size={size}
        color={color}
      />
    ))}
  </View>
);

const SentimentBadge = ({ rating, small }) => {
  const s = sentiment(rating);
  return (
    <View style={{
      flexDirection: 'row', alignItems: 'center',
      backgroundColor: s.color + '20',
      paddingHorizontal: small ? 6 : 8, paddingVertical: small ? 2 : 3,
      borderRadius: 8, gap: 4,
    }}>
      <MaterialCommunityIcons name={s.icon} size={small ? 10 : 12} color={s.color} />
      <Text style={{ fontSize: small ? 9 : 10, color: s.color, fontWeight: '700' }}>
        {s.label}
      </Text>
    </View>
  );
};

const SourceBadge = ({ source }) => {
  const map = {
    google:   { c: '#4285F4', i: 'google' },
    facebook: { c: '#1877F2', i: 'facebook' },
    email:    { c: PURPLE,    i: 'email' },
    internal: { c: PRIMARY,   i: 'application' },
  };
  const m = map[String(source || 'internal').toLowerCase()] || map.internal;
  return (
    <View style={{
      flexDirection: 'row', alignItems: 'center',
      paddingHorizontal: 6, paddingVertical: 2, borderRadius: 6,
      backgroundColor: m.c + '15', gap: 3,
    }}>
      <MaterialCommunityIcons name={m.i} size={10} color={m.c} />
      <Text style={{ fontSize: 9, color: m.c, fontWeight: '700', textTransform: 'capitalize' }}>
        {source || 'internal'}
      </Text>
    </View>
  );
};

const Sparkline = ({ data, color = PRIMARY, height: h = 36, width: w = 120 }) => {
  if (!data || data.length === 0) return <View style={{ height: h, width: w }} />;
  const max = Math.max(...data.map(d => typeof d === 'number' ? d : (d.value || 0)), 1);
  const bw = Math.max(2, (w - (data.length - 1) * 2) / data.length);
  return (
    <View style={{ height: h, width: w, flexDirection: 'row', alignItems: 'flex-end' }}>
      {data.map((d, i) => {
        const v = typeof d === 'number' ? d : (d.value || 0);
        return (
          <View key={i} style={{
            width: bw,
            marginRight: i === data.length - 1 ? 0 : 2,
            height: Math.max(2, (v / max) * h),
            backgroundColor: color, borderRadius: 1,
            opacity: 0.5 + 0.5 * (i / data.length),
          }} />
        );
      })}
    </View>
  );
};

const BarChart = ({ data, valueKey = 'value', labelKey = 'label', color = PRIMARY }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <View style={{ gap: 8 }}>
      {data.map((d, i) => (
        <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <Text style={{ width: 70, fontSize: 11, color: '#475569', fontWeight: '600' }} numberOfLines={1}>
            {d[labelKey]}
          </Text>
          <View style={{ flex: 1, height: 18, backgroundColor: '#F1F5F9', borderRadius: 4, overflow: 'hidden' }}>
            <View style={{
              width: `${(d[valueKey] / max) * 100}%`,
              height: '100%',
              backgroundColor: color,
              borderRadius: 4,
              justifyContent: 'center',
              paddingHorizontal: 6,
            }}>
              {d[valueKey] / max > 0.2 && (
                <Text style={{ fontSize: 10, color: '#FFF', fontWeight: '700' }}>
                  {d.displayValue || d[valueKey]}
                </Text>
              )}
            </View>
          </View>
          {d[valueKey] / max <= 0.2 && (
            <Text style={{ fontSize: 11, color: NEUTRAL, fontWeight: '700' }}>
              {d.displayValue || d[valueKey]}
            </Text>
          )}
        </View>
      ))}
    </View>
  );
};

const Funnel = ({ stages }) => {
  if (!stages || stages.length === 0) return null;
  const max = Math.max(...stages.map(s => s.value), 1);
  return (
    <View style={{ gap: 6 }}>
      {stages.map((s, i) => {
        const pct = (s.value / max) * 100;
        return (
          <View key={i} style={{ alignItems: 'center' }}>
            <View style={{
              width: `${Math.max(20, pct)}%`,
              height: 38,
              backgroundColor: s.color,
              borderRadius: 6,
              justifyContent: 'center',
              alignItems: 'center',
              paddingHorizontal: 8,
            }}>
              <Text style={{ color: '#FFF', fontSize: 12, fontWeight: '700' }} numberOfLines={1}>
                {s.label}: {s.value}
              </Text>
            </View>
            {i < stages.length - 1 && (
              <View style={{
                width: 0, height: 0,
                borderLeftWidth: 6, borderRightWidth: 6, borderTopWidth: 6,
                borderLeftColor: 'transparent', borderRightColor: 'transparent',
                borderTopColor: '#CBD5E1',
                marginTop: 2,
              }} />
            )}
          </View>
        );
      })}
    </View>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════════
export default function RatingsReviewsScreen({ navigation }) {
  // ── Core state ────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading]     = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [activeTab, setActiveTab]     = useState('overview');
  const [reviewsData, setReviewsData] = useState([]);
  const [companyName, setCompanyName] = useState('Our Team');
  const [companyId, setCompanyId]     = useState(0);

  // Search / sort / filter
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [filterOptions, setFilterOptions] = useState({
    rating: 'all', timeframe: 'all', status: 'all',
    sentiment: 'all', source: 'all', responseState: 'all', verified: 'all',
  });

  // Analytics period
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');

  // Modals
  const [showFilterModal,  setShowFilterModal]  = useState(false);
  const [showReplyModal,   setShowReplyModal]   = useState(false);
  const [showReportModal,  setShowReportModal]  = useState(false);
  const [showDetailModal,  setShowDetailModal]  = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [replyText, setReplyText]   = useState('');
  const [requestForm, setRequestForm] = useState({ name: '', email: '', project: '' });

  // FAB
  const [showFAB, setShowFAB] = useState(false);
  const fabAnim = useRef(new Animated.Value(0)).current;

  // Settings (persisted)
  const [settings, setSettings] = useState({
    emailNotifications: true,
    autoResponse: false,
    autoResponseTemplate: 'Thank you for taking the time to leave a review! We truly appreciate your feedback and look forward to working with you again.',
    moderationEnabled: true,
    requireVerification: false,
  });

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => { initialize(); }, []);

  const initialize = useCallback(async () => {
    try {
      setIsLoading(true);
      // Resolve company id + name
      const cid = await getCompanyId();
      setCompanyId(cid);
      try {
        const cp = await AsyncStorage.getItem('companyProfile');
        if (cp) {
          const c = JSON.parse(cp);
          if (c?.name)         setCompanyName(c.name);
          else if (c?.company) setCompanyName(c.company);
        }
      } catch {}

      // Persisted settings
      try {
        const s = await AsyncStorage.getItem('reviews_settings');
        if (s) setSettings(prev => ({ ...prev, ...JSON.parse(s) }));
      } catch {}

      // Try API → fallback to AsyncStorage → fallback to seed data
      let reviews = null;
      try {
        const resp = await apiRequest('customer-reviews');
        if (resp?.success && Array.isArray(resp.data) && resp.data.length > 0) {
          // Map snake_case DB rows → camelCase UI objects
          reviews = resp.data.map(dbToUi).filter(Boolean);
          await AsyncStorage.setItem('customer_reviews', JSON.stringify(reviews));
        }
      } catch {}

      if (!reviews) {
        try {
          const cached = await AsyncStorage.getItem('customer_reviews');
          if (cached) reviews = JSON.parse(cached);
        } catch {}
      }

      if (!reviews) {
        reviews = generateSeedReviews(cid);
        await AsyncStorage.setItem('customer_reviews', JSON.stringify(reviews));
      }

      setReviewsData(reviews);
    } catch (err) {
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[Reviews] initialize:', err?.message);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await initialize();
    setRefreshing(false);
  }, [initialize]);

  // ── Seed reviews (no hardcoded brand name) ────────────────────────────────
  const generateSeedReviews = (cid) => {
    const today = new Date();
    const dayOffset = (n) => {
      const d = new Date(today);
      d.setDate(d.getDate() - n);
      return d.toISOString().split('T')[0];
    };
    return [
      { id: 1, company_id: cid, customerName: 'Sarah Johnson', rating: 5, title: 'Excellent work',
        review: 'Exceeded our expectations with the commercial project. Professional, timely, and delivered exceptional quality. Highly recommended.',
        date: dayOffset(3), project: 'Commercial Office Building', status: 'published',
        helpful: 12, helpfulVoted: false, response: null, verified: true, source: 'internal',
        tags: ['quality', 'professional', 'timely'] },
      { id: 2, company_id: cid, customerName: 'Michael Brown', rating: 4, title: 'Great engineering',
        review: 'Innovative solutions for our infrastructure project. Communication was clear and project completed on schedule.',
        date: dayOffset(8), project: 'Infrastructure Development', status: 'published',
        helpful: 8, helpfulVoted: false,
        response: { text: 'Thank you for your positive feedback. We are glad we could deliver the solutions you needed.',
                    date: dayOffset(7), author: 'Team' },
        verified: true, source: 'google', tags: ['innovation', 'communication'] },
      { id: 3, company_id: cid, customerName: 'Emily Davis', rating: 5, title: 'Outstanding residential project',
        review: 'Our residential development was handled with care and attention to detail. The green building solutions were impressive.',
        date: dayOffset(12), project: 'Residential Development', status: 'published',
        helpful: 15, helpfulVoted: false, response: null, verified: true, source: 'internal',
        tags: ['detail', 'sustainable', 'residential'] },
      { id: 4, company_id: cid, customerName: 'David Wilson', rating: 3, title: 'Good work, minor delays',
        review: 'Quality was good but there were minor delays in the project timeline. Overall satisfied with the final result.',
        date: dayOffset(18), project: 'Commercial Renovation', status: 'published',
        helpful: 5, helpfulVoted: false,
        response: { text: 'We apologise for the delays and appreciate your patience. We have implemented new processes to improve timelines.',
                    date: dayOffset(17), author: 'Team' },
        verified: true, source: 'facebook', tags: ['quality', 'delays'] },
      { id: 5, company_id: cid, customerName: 'Lisa Anderson', rating: 5, title: 'Exceptional service',
        review: 'From planning to completion, the service was exceptional. Their expertise in sustainable construction is remarkable.',
        date: dayOffset(22), project: 'Sustainable Office Complex', status: 'published',
        helpful: 20, helpfulVoted: false, response: null, verified: true, source: 'email',
        tags: ['exceptional', 'sustainable', 'expertise'] },
      { id: 6, company_id: cid, customerName: 'Robert Taylor', rating: 2, title: 'Communication issues',
        review: 'The final result was acceptable but there were significant communication issues that caused confusion during the project.',
        date: dayOffset(30), project: 'Industrial Facility', status: 'pending',
        helpful: 3, helpfulVoted: false, response: null, verified: false, source: 'internal',
        tags: ['communication'] },
    ];
  };

  // ── Persistence ───────────────────────────────────────────────────────────
  // The dynamic-data endpoint accepts SINGLE-ROW operations, not bulk PUTs.
  // We update local state immediately for snappy UX, then sync each affected
  // row individually. AsyncStorage acts as offline cache.
  const persistLocalOnly = useCallback(async (next) => {
    setReviewsData(next);
    try { await AsyncStorage.setItem('customer_reviews', JSON.stringify(next)); } catch {}
  }, []);

  // Save one review row (update existing OR insert new)
  const saveReviewRow = useCallback(async (review) => {
    try {
      const dbRow = uiToDb(review);
      if (review.id && review.id > 0 && Number.isInteger(review.id) && review.id < 1e9) {
        // Existing row — PUT
        await apiRequest(`customer-reviews/${review.id}`, {
          method: 'PUT', body: dbRow,
        });
      } else {
        // New row — POST and pick up the server-assigned id
        const resp = await apiRequest('customer-reviews', { method: 'POST', body: dbRow });
        if (resp?.success && resp.data?.id) {
          // Patch local state with the real id
          setReviewsData(prev => prev.map(r =>
            r === review || r.id === review.id ? { ...r, id: resp.data.id } : r
          ));
          try {
            const stored = await AsyncStorage.getItem('customer_reviews');
            if (stored) {
              const arr = JSON.parse(stored).map(r =>
                r.id === review.id ? { ...r, id: resp.data.id } : r);
              await AsyncStorage.setItem('customer_reviews', JSON.stringify(arr));
            }
          } catch {}
        }
      }
    } catch (err) {
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[Reviews] saveReviewRow failed:', err?.message);
      }
    }
  }, []);

  // Delete one row from the DB
  const deleteReviewRow = useCallback(async (id) => {
    try {
      await apiRequest(`customer-reviews/${id}`, { method: 'DELETE' });
    } catch (err) {
      if (typeof __DEV__ !== 'undefined' && __DEV__) {
        console.log('[Reviews] deleteReviewRow failed:', err?.message);
      }
    }
  }, []);

  // Patch a single review in state + persist that one row to DB
  const updateReviewById = useCallback(async (id, patch) => {
    const next = reviewsData.map(r => r.id === id ? { ...r, ...patch } : r);
    await persistLocalOnly(next);
    const updated = next.find(r => r.id === id);
    if (updated) await saveReviewRow(updated);
  }, [reviewsData, persistLocalOnly, saveReviewRow]);

  const persistSettings = useCallback(async (next) => {
    setSettings(next);
    try { await AsyncStorage.setItem('reviews_settings', JSON.stringify(next)); } catch {}
  }, []);

  // ── Computed analytics ────────────────────────────────────────────────────
  const periodCutoff = useMemo(() => {
    const now = new Date();
    const map = { week: 7, month: 30, quarter: 90, year: 365 };
    if (analyticsPeriod === 'all') return null;
    return new Date(now.getTime() - (map[analyticsPeriod] || 30) * 86400000);
  }, [analyticsPeriod]);

  const overview = useMemo(() => {
    const reviews = reviewsData;
    if (reviews.length === 0) {
      return {
        averageRating: 0, totalReviews: 0,
        ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
        responseRate: 0, averageResponseHours: null, verifiedReviews: 0,
        pulseScore: 0, sentimentBreakdown: { positive: 0, neutral: 0, negative: 0 },
        categories: { quality: 0, communication: 0, timeliness: 0, value: 0, professionalism: 0 },
        funnel: [],
      };
    }
    const total = reviews.length;
    const avg = reviews.reduce((s, r) => s + (parseFloat(r.rating) || 0), 0) / total;
    const dist = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(r => { dist[r.rating] = (dist[r.rating] || 0) + 1; });
    const replied  = reviews.filter(r => r.response).length;
    const verified = reviews.filter(r => r.verified).length;
    const published = reviews.filter(r => r.status === 'published').length;
    const responseRate = total ? Math.round((replied / total) * 100) : 0;
    const verifiedPct  = total ? Math.round((verified / total) * 100) : 0;
    const avgRespHours = computeAvgResponseHours(reviews);

    const sb = { positive: 0, neutral: 0, negative: 0 };
    reviews.forEach(r => { sb[sentiment(r.rating).key] = (sb[sentiment(r.rating).key] || 0) + 1; });

    // Derive category ratings from review text patterns (simple keyword detection)
    const categoryKeywords = {
      quality:         ['quality', 'excellent', 'craftsmanship', 'workmanship'],
      communication:   ['communication', 'communicate', 'responsive', 'updates'],
      timeliness:      ['timely', 'schedule', 'on time', 'delay', 'deadline'],
      value:           ['value', 'price', 'cost', 'worth', 'fair'],
      professionalism: ['professional', 'professionalism', 'expertise', 'team'],
    };
    const cats = {};
    Object.entries(categoryKeywords).forEach(([cat, keys]) => {
      const matching = reviews.filter(r => {
        const text = `${r.review || ''} ${r.title || ''}`.toLowerCase();
        return keys.some(k => text.includes(k));
      });
      cats[cat] = matching.length
        ? matching.reduce((s, r) => s + (parseFloat(r.rating) || 0), 0) / matching.length
        : avg;
    });

    // Pulse Quality Score: composite 0-100
    // rating contribution (50%) + response rate (20%) + verification rate (15%) + sentiment (15%)
    const ratingContrib    = (avg / 5) * 50;
    const responseContrib  = (responseRate / 100) * 20;
    const verifiedContrib  = (verifiedPct / 100) * 15;
    const positivePct      = total ? (sb.positive / total) * 100 : 0;
    const sentimentContrib = (positivePct / 100) * 15;
    const pulseScore = Math.round(ratingContrib + responseContrib + verifiedContrib + sentimentContrib);

    return {
      averageRating: parseFloat(avg.toFixed(1)),
      totalReviews: total,
      ratingDistribution: dist,
      responseRate, repliedCount: replied,
      averageResponseHours: avgRespHours,
      verifiedReviews: verified, verifiedPct, publishedCount: published,
      pulseScore,
      sentimentBreakdown: sb,
      categories: cats,
      funnel: [
        { label: 'Received',  value: total,      color: NEUTRAL },
        { label: 'Verified',  value: verified,   color: INDIGO  },
        { label: 'Published', value: published,  color: PRIMARY },
        { label: 'Replied',   value: replied,    color: SUCCESS },
      ],
    };
  }, [reviewsData]);

  // Monthly trend — last 12 months (real, computed from reviews)
  const monthlyTrend = useMemo(() => {
    const buckets = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const next = new Date(now.getFullYear(), now.getMonth() - i + 1, 1);
      const matching = reviewsData.filter(r => {
        const rd = new Date(r.date);
        return rd >= d && rd < next;
      });
      const avg = matching.length
        ? matching.reduce((s, r) => s + parseFloat(r.rating || 0), 0) / matching.length
        : 0;
      buckets.push({
        month:   d.toLocaleDateString('en-GB', { month: 'short' }),
        reviews: matching.length,
        rating:  parseFloat(avg.toFixed(1)),
      });
    }
    return buckets;
  }, [reviewsData]);

  // Keyword cloud
  const keywords = useMemo(() => extractKeywords(reviewsData), [reviewsData]);

  // Reviews needing response
  const needsResponseCount = useMemo(() =>
    reviewsData.filter(r => !r.response).length, [reviewsData]);

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const filteredReviews = useMemo(() => {
    let list = reviewsData;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(r =>
        (r.customerName || '').toLowerCase().includes(q) ||
        (r.title || '').toLowerCase().includes(q) ||
        (r.review || '').toLowerCase().includes(q) ||
        (r.project || '').toLowerCase().includes(q) ||
        (r.tags || []).some(t => String(t).toLowerCase().includes(q))
      );
    }
    if (filterOptions.rating !== 'all') {
      list = list.filter(r => r.rating === parseInt(filterOptions.rating));
    }
    if (filterOptions.status !== 'all') {
      list = list.filter(r => r.status === filterOptions.status);
    }
    if (filterOptions.sentiment !== 'all') {
      list = list.filter(r => sentiment(r.rating).key === filterOptions.sentiment);
    }
    if (filterOptions.source !== 'all') {
      list = list.filter(r => (r.source || 'internal') === filterOptions.source);
    }
    if (filterOptions.responseState === 'replied') {
      list = list.filter(r => r.response);
    } else if (filterOptions.responseState === 'unreplied') {
      list = list.filter(r => !r.response);
    }
    if (filterOptions.verified === 'verified') {
      list = list.filter(r => r.verified);
    } else if (filterOptions.verified === 'unverified') {
      list = list.filter(r => !r.verified);
    }
    if (filterOptions.timeframe !== 'all') {
      const cutoffDays = { week: 7, month: 30, quarter: 90 }[filterOptions.timeframe];
      if (cutoffDays) {
        list = list.filter(r => daysSince(r.date) <= cutoffDays);
      }
    }

    list = [...list].sort((a, b) => {
      switch (sortBy) {
        case 'date_asc':     return new Date(a.date) - new Date(b.date);
        case 'rating_desc':  return (b.rating || 0) - (a.rating || 0);
        case 'rating_asc':   return (a.rating || 0) - (b.rating || 0);
        case 'helpful_desc': return (b.helpful || 0) - (a.helpful || 0);
        case 'needs_reply':  return (a.response ? 1 : 0) - (b.response ? 1 : 0);
        case 'date_desc':
        default:             return new Date(b.date) - new Date(a.date);
      }
    });
    return list;
  }, [reviewsData, searchQuery, filterOptions, sortBy]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleReply = useCallback(async () => {
    if (!replyText.trim()) {
      Alert.alert('Empty reply', 'Please enter a reply message.');
      return;
    }
    if (!selectedReview) return;
    const author = companyName || 'Team';
    const isEdit = !!selectedReview.response;
    const today  = new Date().toISOString().split('T')[0];
    await updateReviewById(selectedReview.id, {
      response: {
        text: replyText.trim(),
        date: today,
        author,
        edited: isEdit ? today : null,
      },
    });
    setShowReplyModal(false);
    setReplyText('');
    setSelectedReview(null);
    Alert.alert('Saved', isEdit ? 'Reply updated.' : 'Reply posted.');
  }, [replyText, selectedReview, updateReviewById, companyName]);

  const handleDeleteReply = useCallback((review) => {
    Alert.alert('Delete Reply', 'Remove your reply from this review?', [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        await updateReviewById(review.id, { response: null });
      }},
    ]);
  }, [updateReviewById]);

  const toggleHelpful = useCallback(async (review) => {
    const voted = !review.helpfulVoted;
    await updateReviewById(review.id, {
      helpfulVoted: voted,
      helpful: Math.max(0, (review.helpful || 0) + (voted ? 1 : -1)),
    });
  }, [updateReviewById]);

  const toggleVerified = useCallback(async (review) => {
    await updateReviewById(review.id, { verified: !review.verified });
  }, [updateReviewById]);

  const setReviewStatus = useCallback(async (review, status) => {
    await updateReviewById(review.id, { status });
  }, [updateReviewById]);

  const deleteReview = useCallback((review) => {
    Alert.alert('Delete Review', `Delete the review from ${review.customerName}? This cannot be undone.`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        const next = reviewsData.filter(r => r.id !== review.id);
        await persistLocalOnly(next);
        await deleteReviewRow(review.id);
        setShowDetailModal(false);
      }},
    ]);
  }, [reviewsData, persistLocalOnly, deleteReviewRow]);

  const handleReportReview = useCallback((reason) => {
    Alert.alert(
      'Review Reported',
      `Thank you. This review has been flagged for: ${reason}. Our moderation team will look at it shortly.`,
      [{ text: 'OK', onPress: () => setShowReportModal(false) }]
    );
  }, []);

  const sendReviewRequest = useCallback(async () => {
    if (!requestForm.name.trim() || !requestForm.email.trim()) {
      Alert.alert('Missing info', 'Please provide both name and email.');
      return;
    }
    const subject = encodeURIComponent(`We'd love your feedback on the ${requestForm.project || 'project'}`);
    const body = encodeURIComponent(
      `Hi ${requestForm.name},\n\n` +
      `Thank you for working with us on ${requestForm.project || 'your recent project'}. ` +
      `Your feedback would mean a great deal to us and help us improve our service.\n\n` +
      `If you have a moment, please reply with a short review or rating.\n\n` +
      `Best regards,\n${companyName}`
    );
    try {
      await Linking.openURL(`mailto:${requestForm.email}?subject=${subject}&body=${body}`);
      setShowRequestModal(false);
      setRequestForm({ name: '', email: '', project: '' });
    } catch {
      Alert.alert('Email Failed', 'Could not open your email client.');
    }
  }, [requestForm, companyName]);

  // CSV export via Share
  const exportCSV = useCallback(async () => {
    if (reviewsData.length === 0) {
      Alert.alert('No data', 'There are no reviews to export.');
      return;
    }
    const headers = ['ID', 'Customer', 'Date', 'Rating', 'Title', 'Review', 'Project',
                     'Status', 'Verified', 'Source', 'Helpful', 'Has Reply', 'Tags'];
    const rows = reviewsData.map(r => [
      r.id, r.customerName, r.date, r.rating, r.title, r.review, r.project,
      r.status, r.verified ? 'yes' : 'no', r.source || 'internal',
      r.helpful || 0, r.response ? 'yes' : 'no',
      (r.tags || []).join(', '),
    ]);
    const csv = [headers, ...rows].map(row => row.map(escapeCSV).join(',')).join('\n');
    try {
      await Share.share({
        message: csv,
        title:   `${companyName} — Reviews Export (${reviewsData.length} records)`,
      });
    } catch (e) {
      Alert.alert('Export Failed', e?.message || 'Could not share the CSV.');
    }
  }, [reviewsData, companyName]);

  // Plain text report export
  const exportReport = useCallback(async () => {
    const lines = [
      `═══ ${companyName.toUpperCase()} — REVIEW REPORT ═══`,
      `Generated: ${new Date().toLocaleString('en-GB')}`,
      '',
      `Total Reviews:   ${overview.totalReviews}`,
      `Average Rating:  ${overview.averageRating} / 5`,
      `Pulse Score:     ${overview.pulseScore} / 100`,
      `Response Rate:   ${overview.responseRate}%   (${overview.repliedCount} replied)`,
      `Avg Response:    ${fmtHours(overview.averageResponseHours)}`,
      `Verified:        ${overview.verifiedReviews} (${overview.verifiedPct}%)`,
      '',
      `── SENTIMENT ──`,
      `Positive: ${overview.sentimentBreakdown.positive}`,
      `Neutral:  ${overview.sentimentBreakdown.neutral}`,
      `Negative: ${overview.sentimentBreakdown.negative}`,
      '',
      `── RATING DISTRIBUTION ──`,
      ...[5, 4, 3, 2, 1].map(r => `${r}★: ${overview.ratingDistribution[r]}`),
      '',
      `── RECENT REVIEWS ──`,
      ...reviewsData.slice(0, 10).map(r =>
        `• ${r.customerName} (${r.rating}★, ${fmtDate(r.date)})\n  "${r.title}"\n  ${r.review}`
      ),
    ];
    try {
      await Share.share({
        message: lines.join('\n'),
        title:   `${companyName} — Review Report`,
      });
    } catch (e) {
      Alert.alert('Export Failed', e?.message || 'Could not share the report.');
    }
  }, [companyName, overview, reviewsData]);

  // FAB
  const toggleFAB = () => {
    const next = !showFAB;
    Animated.timing(fabAnim, {
      toValue: next ? 1 : 0,
      duration: 200,
      easing: Easing.out(Easing.quad),
      useNativeDriver: true,
    }).start();
    setShowFAB(next);
  };

  // ── Reply templates ───────────────────────────────────────────────────────
  const REPLY_TEMPLATES = [
    { label: 'Thank you (positive)',
      text: `Thank you so much for taking the time to share your experience. We're thrilled to hear it went so well, and we look forward to working with you again!` },
    { label: 'Apology (negative)',
      text: `Thank you for your feedback, and we're sorry to hear about your experience. This isn't the standard we aim for. We'd appreciate the chance to make it right — please reach out to us directly so we can discuss.` },
    { label: 'Acknowledge issue',
      text: `Thank you for letting us know. We hear your concerns and have already taken steps to address them. We appreciate your patience as we work to improve.` },
    { label: 'Refer to project',
      text: `Thank you for the kind words about your project. It was a pleasure working with you and we're delighted you're happy with the result.` },
    { label: 'Invite back',
      text: `We really appreciate your review and the trust you placed in us. If there's anything else we can help you with, please don't hesitate to get in touch.` },
    { label: 'Brief acknowledgement',
      text: `Thank you for your feedback. We genuinely appreciate it.` },
  ];

  // ── Render: tab bar ───────────────────────────────────────────────────────
  const renderTabBar = () => {
    const TABS = [
      { k: 'overview',  l: 'Overview',  i: 'view-dashboard',         n: 0 },
      { k: 'reviews',   l: 'Reviews',   i: 'comment-text-multiple',  n: filteredReviews.length },
      { k: 'analytics', l: 'Analytics', i: 'chart-bar',              n: 0 },
      { k: 'insights',  l: 'Insights',  i: 'lightbulb-on-outline',   n: needsResponseCount },
      { k: 'settings',  l: 'Settings',  i: 'cog',                    n: 0 },
    ];
    return (
      <View style={{
        flexDirection: 'row', backgroundColor: SURFACE,
        borderBottomWidth: 1, borderBottomColor: BORDER,
      }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingHorizontal: 4 }}>
          {TABS.map(t => (
            <TouchableOpacity key={t.k}
              style={[
                { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12,
                  paddingVertical: 10, borderRadius: 14, marginRight: 2, gap: 4 },
                activeTab === t.k && { backgroundColor: '#EFF6FF' },
              ]}
              onPress={() => setActiveTab(t.k)}>
              <MaterialCommunityIcons name={t.i} size={16}
                color={activeTab === t.k ? PRIMARY : '#94A3B8'} />
              <Text style={{
                fontSize: 12,
                color: activeTab === t.k ? PRIMARY : '#94A3B8',
                fontWeight: activeTab === t.k ? '700' : '500',
              }}>{t.l}</Text>
              {t.n > 0 && (
                <View style={{
                  backgroundColor: t.k === 'insights' && t.n > 0 ? DANGER : '#0e4e9620',
                  borderRadius: 8, paddingHorizontal: 5, paddingVertical: 1,
                  minWidth: 18, alignItems: 'center',
                }}>
                  <Text style={{
                    fontSize: 10, fontWeight: '800',
                    color: t.k === 'insights' && t.n > 0 ? '#FFF' : PRIMARY,
                  }}>{t.n}</Text>
                </View>
              )}
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    );
  };

  // ── Render: review card ───────────────────────────────────────────────────
  const renderReviewCard = (item) => {
    const initials = (item.customerName || 'U').split(' ')
      .map(n => n[0]).join('').toUpperCase().slice(0, 2);
    const s = sentiment(item.rating);
    return (
      <TouchableOpacity activeOpacity={0.8}
        style={st.reviewCard}
        onPress={() => { setSelectedReview(item); setShowDetailModal(true); }}
        onLongPress={() => Alert.alert(item.customerName, 'Quick actions', [
          { text: item.response ? 'Edit Reply' : 'Reply',
            onPress: () => { setSelectedReview(item); setReplyText(item.response?.text || ''); setShowReplyModal(true); }},
          { text: item.verified ? 'Unverify' : 'Verify', onPress: () => toggleVerified(item) },
          { text: item.status === 'published' ? 'Move to Pending' : 'Publish',
            onPress: () => setReviewStatus(item, item.status === 'published' ? 'pending' : 'published') },
          { text: 'Delete', style: 'destructive', onPress: () => deleteReview(item) },
          { text: 'Close', style: 'cancel' },
        ])}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          <View style={st.avatar}>
            <Text style={st.avatarTxt}>{initials}</Text>
          </View>
          <View style={{ flex: 1, marginLeft: 10 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
              <Text style={st.custName}>{item.customerName}</Text>
              {item.verified && (
                <MaterialCommunityIcons name="check-decagram" size={14} color={SUCCESS} />
              )}
              <SourceBadge source={item.source} />
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 2 }}>
              <StarRating rating={item.rating} size={12} />
              <Text style={{ fontSize: 11, color: NEUTRAL }}>·</Text>
              <Text style={{ fontSize: 11, color: NEUTRAL }}>{relativeTime(item.date)}</Text>
              <Text style={{ fontSize: 11, color: NEUTRAL }}>·</Text>
              <Text style={{ fontSize: 11, color: NEUTRAL }} numberOfLines={1}>{item.project}</Text>
            </View>
          </View>
          <SentimentBadge rating={item.rating} small />
        </View>

        <Text style={st.revTitle} numberOfLines={1}>{item.title}</Text>
        <Text style={st.revBody} numberOfLines={3}>{item.review}</Text>

        {item.tags?.length > 0 && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
            {item.tags.slice(0, 5).map((tag, i) => (
              <View key={i} style={st.tagChip}>
                <Text style={st.tagTxt}>{tag}</Text>
              </View>
            ))}
          </View>
        )}

        {item.response && (
          <View style={st.replyBox}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 4 }}>
              <MaterialCommunityIcons name="reply" size={12} color={PRIMARY} />
              <Text style={{ fontSize: 11, fontWeight: '700', color: PRIMARY }}>
                {item.response.author}
              </Text>
              <Text style={{ fontSize: 10, color: NEUTRAL }}>· {relativeTime(item.response.date)}</Text>
              {item.response.edited && (
                <Text style={{ fontSize: 10, color: NEUTRAL, fontStyle: 'italic' }}>(edited)</Text>
              )}
            </View>
            <Text style={{ fontSize: 12, color: '#475569', lineHeight: 17 }} numberOfLines={2}>
              {item.response.text}
            </Text>
          </View>
        )}

        <View style={st.cardFoot}>
          <TouchableOpacity style={st.helpfulBtn} onPress={(e) => { e.stopPropagation?.(); toggleHelpful(item); }}>
            <MaterialCommunityIcons
              name={item.helpfulVoted ? 'thumb-up' : 'thumb-up-outline'}
              size={13}
              color={item.helpfulVoted ? PRIMARY : NEUTRAL} />
            <Text style={{
              fontSize: 11, fontWeight: '600',
              color: item.helpfulVoted ? PRIMARY : NEUTRAL,
            }}>Helpful ({item.helpful || 0})</Text>
          </TouchableOpacity>
          {!item.response && (
            <TouchableOpacity style={st.replyBtn}
              onPress={(e) => { e.stopPropagation?.(); setSelectedReview(item); setReplyText(''); setShowReplyModal(true); }}>
              <MaterialCommunityIcons name="reply" size={13} color="#FFF" />
              <Text style={{ fontSize: 11, color: '#FFF', fontWeight: '700' }}>Reply</Text>
            </TouchableOpacity>
          )}
          <View style={[st.statusBadge, {
            backgroundColor: item.status === 'published' ? '#D1FAE5' : '#FEF3C7',
          }]}>
            <Text style={{
              fontSize: 10, fontWeight: '700', textTransform: 'capitalize',
              color: item.status === 'published' ? SUCCESS : WARNING,
            }}>{item.status}</Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  // ── Unified data for FlatList ─────────────────────────────────────────────
  const flatListData = useMemo(() => {
    const data = [];
    if (activeTab === 'overview') {
      data.push(
        { id: 'pulse',      type: 'pulse' },
        { id: 'stats',      type: 'overview-stats' },
        { id: 'quickstats', type: 'quickstats' },
        { id: 'sentiment',  type: 'sentiment' },
        { id: 'recent-h',   type: 'recent-header' },
      );
      reviewsData.slice(0, 3).forEach(r =>
        data.push({ id: `o-${r.id}`, type: 'review', data: r })
      );
    } else if (activeTab === 'reviews') {
      data.push({ id: 'search',  type: 'search'  });
      data.push({ id: 'rev-h',   type: 'reviews-header' });
      if (filteredReviews.length === 0) {
        data.push({ id: 'empty', type: 'empty', icon: 'comment-text-outline', text: 'No reviews match your filters.' });
      } else {
        filteredReviews.forEach(r =>
          data.push({ id: `r-${r.id}`, type: 'review', data: r })
        );
      }
    } else if (activeTab === 'analytics') {
      data.push(
        { id: 'period',    type: 'analytics-period' },
        { id: 'funnel',    type: 'funnel' },
        { id: 'trend',     type: 'trend-chart' },
        { id: 'cats',      type: 'categories' },
      );
    } else if (activeTab === 'insights') {
      data.push(
        { id: 'needs-h',   type: 'needs-header' },
      );
      const needs = reviewsData.filter(r => !r.response);
      if (needs.length === 0) {
        data.push({ id: 'empty-n', type: 'empty', icon: 'checkbox-marked-circle-outline', text: 'All caught up! Every review has a reply.' });
      } else {
        needs.forEach(r => data.push({ id: `n-${r.id}`, type: 'review', data: r }));
      }
      data.push({ id: 'keywords', type: 'keywords' });
    } else if (activeTab === 'settings') {
      data.push(
        { id: 's-notif',   type: 'setting-toggle', key: 'emailNotifications',  icon: 'email',         title: 'Email Notifications', desc: 'Get notified when a new review arrives' },
        { id: 's-auto',    type: 'setting-toggle', key: 'autoResponse',        icon: 'auto-fix',      title: 'Auto-Response',       desc: 'Automatically thank reviewers' },
        { id: 's-tpl',     type: 'setting-template' },
        { id: 's-mod',     type: 'setting-toggle', key: 'moderationEnabled',   icon: 'shield-check',  title: 'Review Moderation',   desc: 'Reviews require approval before publishing' },
        { id: 's-ver',     type: 'setting-toggle', key: 'requireVerification', icon: 'check-decagram',title: 'Require Verification',desc: 'Only show verified reviews publicly' },
        { id: 's-csv',     type: 'setting-action', icon: 'download',  title: 'Export as CSV',   desc: 'Download all reviews as spreadsheet', action: exportCSV },
        { id: 's-pdf',     type: 'setting-action', icon: 'file-document-outline', title: 'Export Report', desc: 'Plain-text summary for sharing', action: exportReport },
        { id: 's-req',     type: 'setting-action', icon: 'email-send', title: 'Request a Review', desc: 'Email a client asking for feedback', action: () => setShowRequestModal(true) },
      );
    }
    return data;
  }, [activeTab, reviewsData, filteredReviews, exportCSV, exportReport]);

  // ── Unified renderer ──────────────────────────────────────────────────────
  const renderItem = ({ item }) => {
    switch (item.type) {
      case 'pulse':
        return (
          <View style={st.pulseCard}>
            <View>
              <Text style={st.pulseLbl}>PULSE QUALITY SCORE</Text>
              <Text style={[st.pulseVal, {
                color: overview.pulseScore >= 80 ? SUCCESS
                     : overview.pulseScore >= 60 ? PRIMARY
                     : overview.pulseScore >= 40 ? WARNING : DANGER,
              }]}>{overview.pulseScore}<Text style={{ fontSize: 16, color: NEUTRAL }}>/100</Text></Text>
              <Text style={st.pulseDesc}>
                {overview.pulseScore >= 80 ? 'Excellent'
                 : overview.pulseScore >= 60 ? 'Good'
                 : overview.pulseScore >= 40 ? 'Needs Work' : 'Critical'}
                {' · '}composite of rating, response rate, verification, and sentiment
              </Text>
            </View>
            <Sparkline data={monthlyTrend.map(m => m.reviews)} color={PRIMARY} height={50} width={90} />
          </View>
        );

      case 'overview-stats':
        return (
          <View style={st.statsCard}>
            <View style={st.avgSection}>
              <Text style={st.avgNum}>{overview.averageRating}</Text>
              <StarRating rating={Math.round(overview.averageRating)} size={20} />
              <Text style={st.avgSub}>Based on {overview.totalReviews} review{overview.totalReviews === 1 ? '' : 's'}</Text>
            </View>
            <View style={{ flex: 1, marginLeft: 16 }}>
              {[5, 4, 3, 2, 1].map(rating => {
                const count = overview.ratingDistribution[rating] || 0;
                const pct   = overview.totalReviews ? (count / overview.totalReviews) * 100 : 0;
                return (
                  <View key={rating} style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4, gap: 4 }}>
                    <Text style={{ fontSize: 11, fontWeight: '600', color: '#475569', width: 8 }}>{rating}</Text>
                    <Ionicons name="star" size={10} color={WARNING} />
                    <View style={{ flex: 1, height: 7, backgroundColor: '#F1F5F9', borderRadius: 4, overflow: 'hidden' }}>
                      <View style={{ width: `${pct}%`, height: '100%', backgroundColor: WARNING }} />
                    </View>
                    <Text style={{ fontSize: 11, color: NEUTRAL, width: 22, textAlign: 'right' }}>{count}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        );

      case 'quickstats':
        return (
          <View style={st.quickGrid}>
            <View style={st.quickCard}>
              <MaterialCommunityIcons name="reply" size={20} color={SUCCESS} />
              <Text style={[st.quickVal, { color: SUCCESS }]}>{overview.responseRate}%</Text>
              <Text style={st.quickLbl}>Response Rate</Text>
            </View>
            <View style={st.quickCard}>
              <MaterialCommunityIcons name="clock-fast" size={20} color={PRIMARY} />
              <Text style={[st.quickVal, { color: PRIMARY }]}>{fmtHours(overview.averageResponseHours)}</Text>
              <Text style={st.quickLbl}>Avg Response</Text>
            </View>
            <View style={st.quickCard}>
              <MaterialCommunityIcons name="check-decagram" size={20} color={PURPLE} />
              <Text style={[st.quickVal, { color: PURPLE }]}>{overview.verifiedReviews}</Text>
              <Text style={st.quickLbl}>Verified ({overview.verifiedPct}%)</Text>
            </View>
            <View style={st.quickCard}>
              <MaterialCommunityIcons name="bell-alert" size={20} color={needsResponseCount > 0 ? DANGER : NEUTRAL} />
              <Text style={[st.quickVal, { color: needsResponseCount > 0 ? DANGER : NEUTRAL }]}>{needsResponseCount}</Text>
              <Text style={st.quickLbl}>Needs Reply</Text>
            </View>
          </View>
        );

      case 'sentiment':
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>Sentiment Breakdown</Text>
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
              {[
                { k: 'positive', l: 'Positive', c: SUCCESS, n: overview.sentimentBreakdown.positive, i: 'emoticon-happy-outline' },
                { k: 'neutral',  l: 'Neutral',  c: WARNING, n: overview.sentimentBreakdown.neutral,  i: 'emoticon-neutral-outline' },
                { k: 'negative', l: 'Negative', c: DANGER,  n: overview.sentimentBreakdown.negative, i: 'emoticon-sad-outline' },
              ].map(s => (
                <View key={s.k} style={[st.sentBox, { borderLeftColor: s.c }]}>
                  <MaterialCommunityIcons name={s.i} size={18} color={s.c} />
                  <Text style={[st.sentNum, { color: s.c }]}>{s.n}</Text>
                  <Text style={st.sentLbl}>{s.l}</Text>
                </View>
              ))}
            </View>
          </View>
        );

      case 'recent-header':
        return (
          <View style={st.subHead}>
            <Text style={st.sectionTitle}>Recent Reviews</Text>
            <TouchableOpacity onPress={() => setActiveTab('reviews')}>
              <Text style={{ fontSize: 12, color: PRIMARY, fontWeight: '700' }}>See all →</Text>
            </TouchableOpacity>
          </View>
        );

      case 'search':
        return (
          <View style={{ paddingHorizontal: 16, paddingTop: 12 }}>
            <View style={st.searchRow}>
              <MaterialCommunityIcons name="magnify" size={18} color={NEUTRAL} />
              <TextInput
                style={st.searchInput}
                placeholder="Search reviews, customers, projects..."
                placeholderTextColor="#94A3B8"
                value={searchQuery}
                onChangeText={setSearchQuery}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity onPress={() => setSearchQuery('')}>
                  <MaterialCommunityIcons name="close-circle" size={16} color="#94A3B8" />
                </TouchableOpacity>
              )}
              <View style={{ width: 1, height: 18, backgroundColor: BORDER, marginHorizontal: 4 }} />
              <TouchableOpacity onPress={() => {
                const orders = ['date_desc', 'date_asc', 'rating_desc', 'rating_asc', 'helpful_desc', 'needs_reply'];
                const idx = orders.indexOf(sortBy);
                setSortBy(orders[(idx + 1) % orders.length]);
              }}>
                <MaterialCommunityIcons name="sort" size={18} color={PRIMARY} />
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowFilterModal(true)}>
                <MaterialCommunityIcons name="filter-variant" size={18} color={PRIMARY} />
              </TouchableOpacity>
            </View>
            <Text style={{ fontSize: 10, color: NEUTRAL, marginTop: 4, fontStyle: 'italic' }}>
              Sorted by: {sortBy.replace(/_/g, ' ')}
            </Text>
          </View>
        );

      case 'reviews-header':
        return (
          <View style={[st.subHead, { paddingTop: 8 }]}>
            <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A' }}>
              {filteredReviews.length} of {reviewsData.length}
            </Text>
            <View style={{ flexDirection: 'row', gap: 4 }}>
              {(searchQuery || Object.values(filterOptions).some(v => v !== 'all')) && (
                <TouchableOpacity style={st.clearBtn} onPress={() => {
                  setSearchQuery('');
                  setFilterOptions({ rating: 'all', timeframe: 'all', status: 'all',
                    sentiment: 'all', source: 'all', responseState: 'all', verified: 'all' });
                }}>
                  <Text style={{ fontSize: 11, color: DANGER, fontWeight: '700' }}>Clear</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        );

      case 'analytics-period':
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>Period</Text>
            <View style={{ flexDirection: 'row', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              {[
                { k: 'week',    l: 'Week' },
                { k: 'month',   l: 'Month' },
                { k: 'quarter', l: 'Quarter' },
                { k: 'year',    l: 'Year' },
                { k: 'all',     l: 'All Time' },
              ].map(p => (
                <TouchableOpacity key={p.k}
                  style={[st.chip, analyticsPeriod === p.k && st.chipActive]}
                  onPress={() => setAnalyticsPeriod(p.k)}>
                  <Text style={[st.chipTxt, analyticsPeriod === p.k && { color: '#FFF' }]}>{p.l}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        );

      case 'funnel':
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>Review Funnel</Text>
            <Text style={st.sectionSub}>Reviews progress through these stages</Text>
            <View style={{ marginTop: 12 }}>
              <Funnel stages={overview.funnel} />
            </View>
          </View>
        );

      case 'trend-chart': {
        const trendData = monthlyTrend.map(m => ({
          label: m.month,
          value: m.reviews,
          displayValue: `${m.reviews}`,
        }));
        const avgs = monthlyTrend.map(m => ({
          label: m.month,
          value: m.rating * 20, // scale 0-5 to 0-100 for bar chart
          displayValue: m.rating > 0 ? `${m.rating}★` : '—',
        }));
        return (
          <>
            <View style={st.sectionCard}>
              <Text style={st.sectionTitle}>Reviews Over Time</Text>
              <Text style={st.sectionSub}>Last 6 months</Text>
              <View style={{ marginTop: 12 }}>
                <BarChart data={trendData} color={PRIMARY} />
              </View>
            </View>
            <View style={st.sectionCard}>
              <Text style={st.sectionTitle}>Average Rating Trend</Text>
              <View style={{ marginTop: 12 }}>
                <BarChart data={avgs} color={WARNING} />
              </View>
            </View>
          </>
        );
      }

      case 'categories': {
        const cats = Object.entries(overview.categories).map(([k, v]) => ({
          label: k.charAt(0).toUpperCase() + k.slice(1),
          value: parseFloat((v || 0).toFixed(1)),
          displayValue: `${(v || 0).toFixed(1)}★`,
        }));
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>Category Performance</Text>
            <Text style={st.sectionSub}>Derived from review keyword analysis</Text>
            <View style={{ marginTop: 12 }}>
              <BarChart data={cats} color={PURPLE} />
            </View>
          </View>
        );
      }

      case 'needs-header':
        return (
          <View style={[st.sectionCard, { backgroundColor: needsResponseCount > 0 ? '#FEE2E2' : '#D1FAE5' }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <MaterialCommunityIcons
                name={needsResponseCount > 0 ? 'alert-circle' : 'check-circle'}
                size={24}
                color={needsResponseCount > 0 ? DANGER : SUCCESS} />
              <View>
                <Text style={{ fontSize: 15, fontWeight: '800', color: '#0F172A' }}>
                  {needsResponseCount} review{needsResponseCount === 1 ? '' : 's'} need{needsResponseCount === 1 ? 's' : ''} a reply
                </Text>
                <Text style={{ fontSize: 11, color: NEUTRAL }}>
                  Responding boosts your Pulse Score and customer trust
                </Text>
              </View>
            </View>
          </View>
        );

      case 'keywords':
        if (keywords.length === 0) return null;
        return (
          <View style={st.sectionCard}>
            <Text style={st.sectionTitle}>Common Themes</Text>
            <Text style={st.sectionSub}>Most-mentioned words in your reviews</Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
              {keywords.slice(0, 15).map(kw => {
                const size = Math.min(16, 11 + Math.floor(kw.count / 2));
                return (
                  <View key={kw.word} style={st.kwChip}>
                    <Text style={{ fontSize: size, fontWeight: '600', color: PRIMARY }}>{kw.word}</Text>
                    <Text style={{ fontSize: 10, color: NEUTRAL, marginLeft: 4 }}>×{kw.count}</Text>
                  </View>
                );
              })}
            </View>
          </View>
        );

      case 'review':
        return renderReviewCard(item.data);

      case 'empty':
        return (
          <View style={st.emptyCard}>
            <MaterialCommunityIcons name={item.icon} size={48} color="#CBD5E1" />
            <Text style={st.emptyTxt}>{item.text}</Text>
          </View>
        );

      case 'setting-toggle':
        return (
          <View style={st.settingRow}>
            <View style={st.settingIcon}>
              <MaterialCommunityIcons name={item.icon} size={20} color={PRIMARY} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={st.settingTitle}>{item.title}</Text>
              <Text style={st.settingDesc}>{item.desc}</Text>
            </View>
            <TouchableOpacity
              onPress={() => persistSettings({ ...settings, [item.key]: !settings[item.key] })}
              style={[
                { width: 44, height: 26, borderRadius: 13, padding: 2, justifyContent: 'center' },
                { backgroundColor: settings[item.key] ? SUCCESS : '#CBD5E1' },
              ]}>
              <View style={{
                width: 22, height: 22, borderRadius: 11, backgroundColor: '#FFF',
                marginLeft: settings[item.key] ? 18 : 0,
              }} />
            </TouchableOpacity>
          </View>
        );

      case 'setting-template':
        if (!settings.autoResponse) return null;
        return (
          <View style={st.settingRow}>
            <View style={st.settingIcon}>
              <MaterialCommunityIcons name="text-box-edit-outline" size={20} color={PRIMARY} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={st.settingTitle}>Auto-Response Template</Text>
              <Text style={st.settingDesc} numberOfLines={2}>{settings.autoResponseTemplate}</Text>
            </View>
            <TouchableOpacity onPress={() => setShowTemplateEditor(true)}>
              <MaterialCommunityIcons name="pencil" size={20} color={PRIMARY} />
            </TouchableOpacity>
          </View>
        );

      case 'setting-action':
        return (
          <TouchableOpacity style={st.settingRow} onPress={item.action}>
            <View style={st.settingIcon}>
              <MaterialCommunityIcons name={item.icon} size={20} color={PRIMARY} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={st.settingTitle}>{item.title}</Text>
              <Text style={st.settingDesc}>{item.desc}</Text>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#94A3B8" />
          </TouchableOpacity>
        );

      default:
        return null;
    }
  };

  // ── Modals ────────────────────────────────────────────────────────────────
  const renderFilterModal = () => (
    <Modal animationType="slide" transparent visible={showFilterModal}
      onRequestClose={() => setShowFilterModal(false)}>
      <View style={st.modalOverlay}>
        <View style={st.modalBox}>
          <View style={st.modalHead}>
            <Text style={st.modalTitle}>Filter Reviews</Text>
            <TouchableOpacity onPress={() => setShowFilterModal(false)}>
              <Ionicons name="close" size={24} color={NEUTRAL} />
            </TouchableOpacity>
          </View>
          <ScrollView style={{ maxHeight: 460 }}>
            {[
              { key: 'rating',        title: 'Rating',
                opts: [['all','All'],['5','5★'],['4','4★'],['3','3★'],['2','2★'],['1','1★']] },
              { key: 'sentiment',     title: 'Sentiment',
                opts: [['all','All'],['positive','Positive'],['neutral','Neutral'],['negative','Negative']] },
              { key: 'timeframe',     title: 'Timeframe',
                opts: [['all','All Time'],['week','Past Week'],['month','Past Month'],['quarter','Past Quarter']] },
              { key: 'status',        title: 'Status',
                opts: [['all','All'],['published','Published'],['pending','Pending']] },
              { key: 'source',        title: 'Source',
                opts: [['all','All'],['internal','Internal'],['google','Google'],['facebook','Facebook'],['email','Email']] },
              { key: 'responseState', title: 'Response',
                opts: [['all','All'],['unreplied','Needs Reply'],['replied','Replied']] },
              { key: 'verified',      title: 'Verification',
                opts: [['all','All'],['verified','Verified Only'],['unverified','Unverified']] },
            ].map(sec => (
              <View key={sec.key} style={{ marginBottom: 14 }}>
                <Text style={st.filterSecTitle}>{sec.title}</Text>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
                  {sec.opts.map(([val, lbl]) => (
                    <TouchableOpacity key={val}
                      style={[st.filterChip, filterOptions[sec.key] === val && st.filterChipOn]}
                      onPress={() => setFilterOptions({ ...filterOptions, [sec.key]: val })}>
                      <Text style={[
                        { fontSize: 12, color: NEUTRAL, fontWeight: '600' },
                        filterOptions[sec.key] === val && { color: '#FFF' },
                      ]}>{lbl}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </View>
            ))}
          </ScrollView>
          <TouchableOpacity style={st.applyBtn} onPress={() => setShowFilterModal(false)}>
            <Text style={{ color: '#FFF', fontSize: 15, fontWeight: '700' }}>Apply Filters</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const renderReplyModal = () => (
    <Modal animationType="slide" transparent visible={showReplyModal}
      onRequestClose={() => setShowReplyModal(false)}>
      <View style={st.modalOverlay}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={st.modalBox}>
          <View style={st.modalHead}>
            <Text style={st.modalTitle}>{selectedReview?.response ? 'Edit Reply' : 'Reply to Review'}</Text>
            <TouchableOpacity onPress={() => setShowReplyModal(false)}>
              <Ionicons name="close" size={24} color={NEUTRAL} />
            </TouchableOpacity>
          </View>
          {selectedReview && (
            <ScrollView style={{ maxHeight: 540 }}>
              <View style={st.origReviewBox}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text style={{ fontSize: 12, fontWeight: '700', color: '#475569' }}>
                    {selectedReview.customerName}
                  </Text>
                  <StarRating rating={selectedReview.rating} size={12} />
                </View>
                <Text style={{ fontSize: 13, color: '#475569', fontStyle: 'italic' }} numberOfLines={3}>
                  "{selectedReview.review}"
                </Text>
              </View>

              <Text style={st.formLbl}>Quick Templates</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}
                contentContainerStyle={{ gap: 6, paddingVertical: 4 }}>
                {REPLY_TEMPLATES.map(t => (
                  <TouchableOpacity key={t.label}
                    style={st.tplChip}
                    onPress={() => setReplyText(t.text)}>
                    <Text style={{ fontSize: 11, color: PRIMARY, fontWeight: '600' }}>{t.label}</Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>

              <TouchableOpacity style={st.aiSuggestBtn} onPress={() => {
                // Simple "AI" suggestion = pick template based on sentiment
                const s = sentiment(selectedReview.rating);
                const pick = s.key === 'positive' ? REPLY_TEMPLATES[0]
                           : s.key === 'neutral'  ? REPLY_TEMPLATES[2]
                           : REPLY_TEMPLATES[1];
                setReplyText(pick.text);
              }}>
                <MaterialCommunityIcons name="auto-fix" size={14} color={PURPLE} />
                <Text style={{ fontSize: 12, color: PURPLE, fontWeight: '700' }}>
                  Suggest reply based on sentiment
                </Text>
              </TouchableOpacity>

              <Text style={st.formLbl}>Your Reply</Text>
              <TextInput
                style={st.replyInput}
                value={replyText}
                onChangeText={setReplyText}
                placeholder={`Type your response as ${companyName}...`}
                placeholderTextColor="#94A3B8"
                multiline
                numberOfLines={5}
                textAlignVertical="top"
              />
              <Text style={{ fontSize: 10, color: NEUTRAL, marginTop: 4 }}>
                {replyText.length} character{replyText.length === 1 ? '' : 's'} · will post as "{companyName}"
              </Text>
            </ScrollView>
          )}
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 14 }}>
            {selectedReview?.response && (
              <TouchableOpacity style={[st.applyBtn, { flex: 1, backgroundColor: DANGER }]}
                onPress={() => {
                  setShowReplyModal(false);
                  handleDeleteReply(selectedReview);
                }}>
                <Text style={{ color: '#FFF', fontWeight: '700' }}>Delete</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity style={[st.applyBtn, { flex: 2 }]} onPress={handleReply}>
              <Text style={{ color: '#FFF', fontSize: 15, fontWeight: '700' }}>
                {selectedReview?.response ? 'Update Reply' : 'Send Reply'}
              </Text>
            </TouchableOpacity>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  const renderReportModal = () => (
    <Modal animationType="slide" transparent visible={showReportModal}
      onRequestClose={() => setShowReportModal(false)}>
      <View style={st.modalOverlay}>
        <View style={st.modalBox}>
          <View style={st.modalHead}>
            <Text style={st.modalTitle}>Report Review</Text>
            <TouchableOpacity onPress={() => setShowReportModal(false)}>
              <Ionicons name="close" size={24} color={NEUTRAL} />
            </TouchableOpacity>
          </View>
          <Text style={{ fontSize: 13, color: NEUTRAL, marginBottom: 12 }}>
            Why are you reporting this review?
          </Text>
          {[
            'Inappropriate content', 'Spam or fake review', 'Offensive language',
            'Misleading information', 'Violation of terms', 'Other',
          ].map(reason => (
            <TouchableOpacity key={reason}
              style={st.reportOpt}
              onPress={() => handleReportReview(reason)}>
              <Text style={{ fontSize: 14, color: '#1E293B', flex: 1 }}>{reason}</Text>
              <MaterialCommunityIcons name="chevron-right" size={20} color="#94A3B8" />
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </Modal>
  );

  const renderDetailModal = () => {
    if (!selectedReview) return null;
    const item = selectedReview;
    const sameCustomer = reviewsData.filter(r =>
      r.customerName === item.customerName && r.id !== item.id);
    return (
      <Modal animationType="slide" transparent visible={showDetailModal}
        onRequestClose={() => setShowDetailModal(false)}>
        <View style={st.modalOverlay}>
          <View style={[st.modalBox, { maxHeight: '88%' }]}>
            <View style={st.modalHead}>
              <Text style={st.modalTitle} numberOfLines={1}>{item.customerName}</Text>
              <TouchableOpacity onPress={() => setShowDetailModal(false)}>
                <Ionicons name="close" size={24} color={NEUTRAL} />
              </TouchableOpacity>
            </View>
            <ScrollView>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <StarRating rating={item.rating} size={18} />
                <SentimentBadge rating={item.rating} />
              </View>
              <View style={{ flexDirection: 'row', gap: 6, marginBottom: 12 }}>
                <SourceBadge source={item.source} />
                {item.verified && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 3,
                    backgroundColor: '#D1FAE5', paddingHorizontal: 6, borderRadius: 6 }}>
                    <MaterialCommunityIcons name="check-decagram" size={11} color={SUCCESS} />
                    <Text style={{ fontSize: 10, color: SUCCESS, fontWeight: '700' }}>Verified</Text>
                  </View>
                )}
              </View>
              <Text style={st.detailTitle}>{item.title}</Text>
              <Text style={st.detailRev}>{item.review}</Text>
              <Text style={{ fontSize: 11, color: NEUTRAL, marginTop: 8 }}>
                {fmtDate(item.date)} · {item.project}
              </Text>

              {item.tags?.length > 0 && (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
                  {item.tags.map((tag, i) => (
                    <View key={i} style={st.tagChip}><Text style={st.tagTxt}>{tag}</Text></View>
                  ))}
                </View>
              )}

              {item.response && (
                <View style={[st.replyBox, { marginTop: 14 }]}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                    <MaterialCommunityIcons name="reply" size={14} color={PRIMARY} />
                    <Text style={{ fontSize: 12, fontWeight: '700', color: PRIMARY }}>
                      {item.response.author}
                    </Text>
                    <Text style={{ fontSize: 10, color: NEUTRAL }}>
                      · {fmtDate(item.response.date)}
                    </Text>
                  </View>
                  <Text style={{ fontSize: 13, color: '#475569', lineHeight: 19 }}>
                    {item.response.text}
                  </Text>
                </View>
              )}

              {sameCustomer.length > 0 && (
                <View style={{ marginTop: 16 }}>
                  <Text style={st.detailSecTitle}>
                    Other reviews from {item.customerName} ({sameCustomer.length})
                  </Text>
                  {sameCustomer.slice(0, 3).map(r => (
                    <View key={r.id} style={{ paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <StarRating rating={r.rating} size={11} />
                        <Text style={{ fontSize: 10, color: NEUTRAL }}>{relativeTime(r.date)}</Text>
                      </View>
                      <Text style={{ fontSize: 12, color: '#475569', marginTop: 2 }} numberOfLines={2}>
                        "{r.review}"
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              <View style={{ flexDirection: 'row', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                <TouchableOpacity style={[st.detailAction, { backgroundColor: PRIMARY }]}
                  onPress={() => {
                    setShowDetailModal(false);
                    setReplyText(item.response?.text || '');
                    setShowReplyModal(true);
                  }}>
                  <MaterialCommunityIcons name="reply" size={14} color="#FFF" />
                  <Text style={st.detailActionTxt}>{item.response ? 'Edit Reply' : 'Reply'}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[st.detailAction, { backgroundColor: item.verified ? NEUTRAL : SUCCESS }]}
                  onPress={() => toggleVerified(item)}>
                  <MaterialCommunityIcons name="check-decagram" size={14} color="#FFF" />
                  <Text style={st.detailActionTxt}>{item.verified ? 'Unverify' : 'Verify'}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[st.detailAction, { backgroundColor: WARNING }]}
                  onPress={() => {
                    setShowDetailModal(false);
                    setShowReportModal(true);
                  }}>
                  <MaterialCommunityIcons name="flag" size={14} color="#FFF" />
                  <Text style={st.detailActionTxt}>Report</Text>
                </TouchableOpacity>
                <TouchableOpacity style={[st.detailAction, { backgroundColor: DANGER }]}
                  onPress={() => deleteReview(item)}>
                  <MaterialCommunityIcons name="delete" size={14} color="#FFF" />
                  <Text style={st.detailActionTxt}>Delete</Text>
                </TouchableOpacity>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  const renderRequestModal = () => (
    <Modal animationType="slide" transparent visible={showRequestModal}
      onRequestClose={() => setShowRequestModal(false)}>
      <View style={st.modalOverlay}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={st.modalBox}>
          <View style={st.modalHead}>
            <Text style={st.modalTitle}>Request a Review</Text>
            <TouchableOpacity onPress={() => setShowRequestModal(false)}>
              <Ionicons name="close" size={24} color={NEUTRAL} />
            </TouchableOpacity>
          </View>
          <Text style={{ fontSize: 13, color: NEUTRAL, marginBottom: 12 }}>
            We'll compose an email asking the client to leave a review. You can customise before sending.
          </Text>
          <Text style={st.formLbl}>Client Name *</Text>
          <TextInput style={st.formInput} value={requestForm.name}
            onChangeText={t => setRequestForm({ ...requestForm, name: t })}
            placeholder="e.g. Sarah Johnson" placeholderTextColor="#94A3B8" />
          <Text style={st.formLbl}>Client Email *</Text>
          <TextInput style={st.formInput} value={requestForm.email}
            onChangeText={t => setRequestForm({ ...requestForm, email: t })}
            placeholder="email@example.com" placeholderTextColor="#94A3B8"
            keyboardType="email-address" autoCapitalize="none" />
          <Text style={st.formLbl}>Project (optional)</Text>
          <TextInput style={st.formInput} value={requestForm.project}
            onChangeText={t => setRequestForm({ ...requestForm, project: t })}
            placeholder="e.g. Commercial Office Building" placeholderTextColor="#94A3B8" />
          <TouchableOpacity style={st.applyBtn} onPress={sendReviewRequest}>
            <MaterialCommunityIcons name="email-send" size={18} color="#FFF" />
            <Text style={{ color: '#FFF', fontSize: 15, fontWeight: '700' }}>Compose Email</Text>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  const renderTemplateEditorModal = () => (
    <Modal animationType="slide" transparent visible={showTemplateEditor}
      onRequestClose={() => setShowTemplateEditor(false)}>
      <View style={st.modalOverlay}>
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={st.modalBox}>
          <View style={st.modalHead}>
            <Text style={st.modalTitle}>Auto-Response Template</Text>
            <TouchableOpacity onPress={() => setShowTemplateEditor(false)}>
              <Ionicons name="close" size={24} color={NEUTRAL} />
            </TouchableOpacity>
          </View>
          <Text style={{ fontSize: 13, color: NEUTRAL, marginBottom: 12 }}>
            This message will be sent automatically as a reply to new reviews.
          </Text>
          <TextInput style={[st.formInput, { height: 140, textAlignVertical: 'top' }]}
            value={settings.autoResponseTemplate}
            onChangeText={t => setSettings({ ...settings, autoResponseTemplate: t })}
            multiline
            placeholder="Type your default reply..." placeholderTextColor="#94A3B8" />
          <TouchableOpacity style={st.applyBtn}
            onPress={() => {
              persistSettings(settings);
              setShowTemplateEditor(false);
              Alert.alert('Saved', 'Template updated.');
            }}>
            <Text style={{ color: '#FFF', fontSize: 15, fontWeight: '700' }}>Save Template</Text>
          </TouchableOpacity>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );

  // ── Loading ───────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <SafeAreaView style={st.screen}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <ActivityIndicator size="large" color={PRIMARY} />
          <Text style={{ marginTop: 12, color: NEUTRAL, fontWeight: '500' }}>
            Loading ratings and reviews...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={st.screen}>
      <StatusBar barStyle="light-content" backgroundColor={PRIMARY} />
      <LinearGradient colors={[PRIMARY, PRIMARY_2]} style={st.header}>
        <View style={st.headerRow}>
          <TouchableOpacity onPress={() => navigation?.goBack?.()} style={st.hBtn}>
            <Ionicons name="arrow-back" size={22} color="#FFF" />
          </TouchableOpacity>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <Text style={st.hTitle}>Ratings & Reviews</Text>
            <Text style={st.hSub}>{companyName}</Text>
          </View>
          <TouchableOpacity onPress={() => setShowFilterModal(true)} style={st.hBtn}>
            <MaterialCommunityIcons name="filter-variant" size={20} color="#FFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {renderTabBar()}

      <FlatList
        data={flatListData}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]} />}
        contentContainerStyle={{ paddingBottom: 100 }}
        removeClippedSubviews
        windowSize={11}
        initialNumToRender={6}
      />

      {/* FAB */}
      <View style={st.fabContainer} pointerEvents="box-none">
        {showFAB && (
          <Animated.View style={[st.fabMenu, {
            opacity: fabAnim,
            transform: [{ scale: fabAnim }, {
              translateY: fabAnim.interpolate({ inputRange: [0, 1], outputRange: [20, 0] }),
            }],
          }]} pointerEvents="auto">
            {[
              { icon: 'email-send', label: 'Request Review', color: PRIMARY,
                onPress: () => { toggleFAB(); setShowRequestModal(true); }},
              { icon: 'download',   label: 'Export CSV',     color: SUCCESS,
                onPress: () => { toggleFAB(); exportCSV(); }},
              { icon: 'file-document-outline', label: 'Export Report', color: INFO,
                onPress: () => { toggleFAB(); exportReport(); }},
              { icon: 'cog',        label: 'Settings',       color: NEUTRAL,
                onPress: () => { toggleFAB(); setActiveTab('settings'); }},
            ].map(it => (
              <TouchableOpacity key={it.label} style={st.fabItem} onPress={it.onPress}>
                <Text style={st.fabItemLbl}>{it.label}</Text>
                <View style={[st.fabItemBtn, { backgroundColor: it.color }]}>
                  <MaterialCommunityIcons name={it.icon} size={18} color="#FFF" />
                </View>
              </TouchableOpacity>
            ))}
          </Animated.View>
        )}
        <TouchableOpacity style={[st.fab, { backgroundColor: showFAB ? DANGER : PRIMARY }]}
          onPress={toggleFAB} activeOpacity={0.85}>
          <MaterialCommunityIcons name={showFAB ? 'close' : 'plus'} size={26} color="#FFF" />
        </TouchableOpacity>
      </View>

      {renderFilterModal()}
      {renderReplyModal()}
      {renderReportModal()}
      {renderDetailModal()}
      {renderRequestModal()}
      {renderTemplateEditorModal()}
    </SafeAreaView>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// STYLES
// ════════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  screen: { flex: 1, backgroundColor: BG },
  header: { paddingHorizontal: 16, paddingVertical: 12,
    paddingTop: Platform.OS === 'ios' ? 50 : 14 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn: { width: 36, height: 36, borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.15)', alignItems: 'center', justifyContent: 'center' },
  hTitle: { fontSize: 18, fontWeight: '800', color: '#FFF' },
  hSub:   { fontSize: 11, color: '#BFDBFE', marginTop: 1 },

  pulseCard: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    backgroundColor: SURFACE, margin: 16, marginBottom: 8, padding: 16, borderRadius: 12,
    borderWidth: 1, borderColor: BORDER },
  pulseLbl:  { fontSize: 10, color: NEUTRAL, fontWeight: '700' },
  pulseVal:  { fontSize: 36, fontWeight: '800', marginTop: 2 },
  pulseDesc: { fontSize: 11, color: NEUTRAL, marginTop: 4, maxWidth: 220 },

  statsCard: { flexDirection: 'row', backgroundColor: SURFACE,
    marginHorizontal: 16, marginBottom: 8, padding: 16, borderRadius: 12,
    borderWidth: 1, borderColor: BORDER },
  avgSection: { alignItems: 'center', width: 100 },
  avgNum: { fontSize: 38, fontWeight: '800', color: '#0F172A' },
  avgSub: { fontSize: 10, color: NEUTRAL, marginTop: 4, textAlign: 'center' },

  quickGrid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: 16,
    marginBottom: 8, gap: 8 },
  quickCard: { width: (width - 48) / 2, backgroundColor: SURFACE, padding: 12,
    borderRadius: 12, borderWidth: 1, borderColor: BORDER, alignItems: 'center' },
  quickVal: { fontSize: 18, fontWeight: '800', marginTop: 6 },
  quickLbl: { fontSize: 10, color: NEUTRAL, marginTop: 2, fontWeight: '600' },

  sectionCard: { backgroundColor: SURFACE, marginHorizontal: 16, marginBottom: 8,
    padding: 14, borderRadius: 12, borderWidth: 1, borderColor: BORDER },
  sectionTitle: { fontSize: 15, fontWeight: '800', color: '#0F172A' },
  sectionSub: { fontSize: 11, color: NEUTRAL, marginTop: 2 },
  subHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 16, paddingBottom: 4, paddingTop: 6 },

  sentBox: { flex: 1, alignItems: 'center', padding: 10, borderRadius: 10,
    backgroundColor: '#F8FAFC', borderLeftWidth: 3 },
  sentNum: { fontSize: 22, fontWeight: '800', marginTop: 4 },
  sentLbl: { fontSize: 11, color: NEUTRAL, fontWeight: '600' },

  searchRow: { flexDirection: 'row', alignItems: 'center', gap: 8,
    backgroundColor: SURFACE, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8,
    borderWidth: 1, borderColor: BORDER },
  searchInput: { flex: 1, fontSize: 14, color: '#0F172A', paddingVertical: 0 },

  clearBtn: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6,
    backgroundColor: '#FEE2E2' },

  reviewCard: { backgroundColor: SURFACE, marginHorizontal: 16, marginBottom: 8,
    padding: 14, borderRadius: 12, borderWidth: 1, borderColor: BORDER },
  avatar: { width: 42, height: 42, borderRadius: 21, backgroundColor: '#E0E7FF',
    alignItems: 'center', justifyContent: 'center' },
  avatarTxt: { fontSize: 14, fontWeight: '800', color: PRIMARY },
  custName: { fontSize: 14, fontWeight: '700', color: '#1E293B' },
  revTitle: { fontSize: 14, fontWeight: '700', color: '#0F172A', marginTop: 10 },
  revBody: { fontSize: 13, color: '#475569', marginTop: 4, lineHeight: 18 },
  tagChip: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 10,
    backgroundColor: '#EFF6FF' },
  tagTxt: { fontSize: 10, color: PRIMARY, fontWeight: '600', textTransform: 'capitalize' },
  replyBox: { backgroundColor: '#F8FAFC', padding: 10, borderRadius: 8,
    marginTop: 10, borderLeftWidth: 3, borderLeftColor: PRIMARY },
  cardFoot: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginTop: 10, gap: 6, flexWrap: 'wrap' },
  helpfulBtn: { flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 8, paddingVertical: 4 },
  replyBtn: { flexDirection: 'row', alignItems: 'center', gap: 4,
    backgroundColor: PRIMARY, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6 },

  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14,
    backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: BORDER },
  chipActive: { backgroundColor: PRIMARY, borderColor: PRIMARY },
  chipTxt: { fontSize: 12, color: NEUTRAL, fontWeight: '600' },

  kwChip: { flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14,
    backgroundColor: '#EFF6FF', borderWidth: 1, borderColor: '#DBEAFE' },

  emptyCard: { alignItems: 'center', padding: 36, marginHorizontal: 16, marginTop: 12 },
  emptyTxt: { fontSize: 14, color: NEUTRAL, marginTop: 10, fontWeight: '500', textAlign: 'center' },

  settingRow: { flexDirection: 'row', alignItems: 'center',
    backgroundColor: SURFACE, marginHorizontal: 16, marginBottom: 6,
    padding: 12, borderRadius: 12, borderWidth: 1, borderColor: BORDER, gap: 10 },
  settingIcon: { width: 36, height: 36, borderRadius: 10,
    backgroundColor: '#EFF6FF', alignItems: 'center', justifyContent: 'center' },
  settingTitle: { fontSize: 14, fontWeight: '700', color: '#0F172A' },
  settingDesc: { fontSize: 11, color: NEUTRAL, marginTop: 2 },

  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end' },
  modalBox: { backgroundColor: '#FFF', borderTopLeftRadius: 20, borderTopRightRadius: 20,
    padding: 20, maxHeight: '92%' },
  modalHead: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 12 },
  modalTitle: { fontSize: 17, fontWeight: '800', color: '#0F172A' },

  filterSecTitle: { fontSize: 12, fontWeight: '700', color: '#475569',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  filterChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14,
    backgroundColor: '#F1F5F9', borderWidth: 1, borderColor: BORDER },
  filterChipOn: { backgroundColor: PRIMARY, borderColor: PRIMARY },

  applyBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
    backgroundColor: PRIMARY, borderRadius: 12, paddingVertical: 14, gap: 6, marginTop: 8 },

  origReviewBox: { backgroundColor: '#F1F5F9', padding: 10, borderRadius: 8,
    marginBottom: 12, borderLeftWidth: 3, borderLeftColor: PRIMARY },
  formLbl: { fontSize: 13, fontWeight: '600', color: '#334155', marginBottom: 6, marginTop: 8 },
  formInput: { backgroundColor: '#F8FAFC', borderWidth: 1.5, borderColor: BORDER,
    borderRadius: 10, paddingHorizontal: 14, paddingVertical: 10, fontSize: 14, color: '#0F172A' },
  replyInput: { backgroundColor: '#F8FAFC', borderWidth: 1.5, borderColor: BORDER,
    borderRadius: 10, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, color: '#0F172A',
    minHeight: 90, textAlignVertical: 'top' },
  tplChip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 14,
    backgroundColor: '#EFF6FF', borderWidth: 1, borderColor: '#DBEAFE' },
  aiSuggestBtn: { flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#F5F3FF', paddingHorizontal: 10, paddingVertical: 8, borderRadius: 8,
    marginTop: 8, alignSelf: 'flex-start' },

  reportOpt: { flexDirection: 'row', alignItems: 'center',
    paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: BORDER },

  detailTitle: { fontSize: 16, fontWeight: '800', color: '#0F172A', marginTop: 8 },
  detailRev: { fontSize: 14, color: '#475569', marginTop: 6, lineHeight: 20 },
  detailSecTitle: { fontSize: 12, fontWeight: '700', color: '#475569',
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  detailAction: { flexDirection: 'row', alignItems: 'center', gap: 4,
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8 },
  detailActionTxt: { color: '#FFF', fontSize: 12, fontWeight: '700' },

  fabContainer: { position: 'absolute', bottom: 20, right: 20, alignItems: 'flex-end' },
  fab: { width: 56, height: 56, borderRadius: 28, alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.3, shadowRadius: 6,
    elevation: 8 },
  fabMenu: { alignItems: 'flex-end', marginBottom: 10, gap: 10 },
  fabItem: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  fabItemLbl: { backgroundColor: SURFACE, paddingHorizontal: 10, paddingVertical: 5,
    borderRadius: 8, fontSize: 12, fontWeight: '600', color: '#1E293B',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.15,
    shadowRadius: 3, elevation: 3 },
  fabItemBtn: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center',
    shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.2,
    shadowRadius: 4, elevation: 5 },
});
